import React, { useState, useCallback, useEffect, useRef, useId } from "react";
import { Link } from "react-router-dom";
import { RecommendationTargetCall, RecommendationOverrides } from "../../../services/grantsApi";
import { StatusBadge, StatusType } from "../../common/StatusBadge";
import MultiSelectDropdown, { Option } from "../../common/MultiSelectDropdown";
import { TARGET_GROUP_LABELS } from "../../../constants/grants.constants";
import { articleRoleLabels } from "../shared/articleRoleLabels";
import {
  ExtractionStatus,
  callInstantToWarsawParts,
  dateValuesEqual,
  hasOwn,
  llmWallTimeToWarsawParts,
  numericValuesEqual,
  regionSetsEqual,
  statusValuesEqual,
  toFiniteNumber,
} from "./fieldChangesNormalization";

const DATE_FIELDS = ["applicationStartDate", "applicationEndDate"];

/**
 * Field labels in Polish
 */
const FIELD_LABELS: Record<string, string> = {
  applicationStartDate: "Data rozpoczęcia",
  applicationEndDate: "Data zakończenia",
  allocation: "Alokacja",
  targetRegions: "Regiony",
  targetGroups: "Grupy docelowe",
  maxParticipants: "Maks. uczestników",
  callNumber: "Numer naboru",
  title: "Tytuł",
  status: "Status naboru",
  articleCharacter: "Charakter artykułu",
};

/**
 * Article character options for the select dropdown.
 * Derived from the shared articleRoleLabels map (single source of truth) so
 * new ArticleType enum values appear automatically.
 */
const ARTICLE_CHARACTER_OPTIONS = Object.entries(articleRoleLabels).map(
  ([value, label]) => ({ value, label })
);

/**
 * Short Polish descriptions for article character options (shown as tooltips on <option>).
 * Helps moderators distinguish visually-similar types (e.g. CALL_CANCELLATION vs CALL_CLOSURE,
 * DEADLINE_CHANGE vs CALL_EXTENSION).
 */
const ARTICLE_CHARACTER_DESCRIPTIONS: Record<string, string> = {
  CALL_ANNOUNCEMENT: "Pierwsze oficjalne ogłoszenie o otwarciu naboru",
  CALL_UPDATE: "Aktualizacja informacji o trwającym naborze",
  CALL_EXTENSION: "Oficjalne przedłużenie terminu składania wniosków przez organizatora",
  CALL_SUSPENSION: "Czasowe zawieszenie naboru — nabór może zostać wznowiony",
  CALL_RESULTS: "Informacja o wynikach zakończonego naboru",
  CALL_CLOSURE: "Naturalne zamknięcie naboru po zakończeniu jego trwania",
  PROJECT_INFO: "Ogólne informacje o projekcie lub działaniu",
  OTHER: "Inny rodzaj artykułu — nie pasuje do żadnej kategorii",
  INFO_MEETING: "Zaproszenie lub relacja ze spotkania informacyjnego dla wnioskodawców",
  CALL_CANCELLATION: "Odwołanie naboru przed jego zakończeniem — nabór nie zostanie wznowiony",
  DEADLINE_CHANGE: "Zmiana terminu naboru ogłoszona przez LLM (sygnał do weryfikacji; wybierz CALL_EXTENSION jeśli to przedłużenie)",
};

/**
 * Extraction status labels in Polish (for extracted data snapshot status field).
 * Keyed by ExtractionStatus, so the compiler keeps it in step with
 * EXTRACTION_STATUS_TO_CALL_STATUS; exported for the completeness test (AC 4).
 */
export const EXTRACTION_STATUS_LABELS: Record<ExtractionStatus, string> = {
  OPENED: "Otwarty",
  CLOSED: "Zamknięty",
  CANCELLED: "Odwołany",
  SUSPENDED: "Wstrzymany",
  RESUMED: "Wznowiony",
  EXTENDED: "Przedłużony",
  UNDER_REVIEW: "Rozpatrywany",
  RESULTS: "Wyniki",
  UPDATE: "Aktualizacja",
};

/**
 * CallStatus labels in Polish, for the "Obecna wartość" column: the target call
 * carries a CallStatus (OPEN, RESOLVED, ...), not the snapshot vocabulary.
 *
 * WHY a separate dictionary: it deliberately reuses the EXTRACTION wording, so that
 * "Obecna" and "Nowa" in one row use the same words (SUSPENDED "Wstrzymany" on both
 * sides). It deliberately differs from CALL_STATUS_LABELS in constants/grants-labels.ts
 * (SUSPENDED "Zawieszony", CANCELLED "Anulowany"); unifying the dictionaries app-wide
 * is a separate follow-up.
 * @see apps/backend/src/entities/grants-calls-grant-call.entity.ts (CallStatus)
 */
const DIFF_CALL_STATUS_LABELS: Record<string, string> = {
  OPEN: "Otwarty",
  ANNOUNCED: "Ogłoszony",
  SUSPENDED: "Wstrzymany",
  CLOSED: "Zamknięty",
  CANCELLED: "Odwołany",
  UNDER_REVIEW: "Rozpatrywany",
  RESOLVED: "Rozstrzygnięty",
  EXPIRED: "Wygasły",
};

/**
 * Badge colour of a CallStatus = colour of its extraction counterpart (same reason
 * as DIFF_CALL_STATUS_LABELS; intentionally not CALL_STATUS_COLORS from grants-labels.ts).
 * ANNOUNCED and EXPIRED have none, so they get the neutral (gray) badge.
 */
const DIFF_CALL_STATUS_BADGE_TYPE: Record<string, StatusType> = {
  OPEN: "extraction-OPENED",
  ANNOUNCED: "unknown",
  SUSPENDED: "extraction-SUSPENDED",
  CLOSED: "extraction-CLOSED",
  CANCELLED: "extraction-CANCELLED",
  UNDER_REVIEW: "extraction-UNDER_REVIEW",
  RESOLVED: "extraction-RESULTS",
  EXPIRED: "unknown",
};

/**
 * Extraction status options for the select dropdown
 */
const EXTRACTION_STATUS_OPTIONS = Object.entries(EXTRACTION_STATUS_LABELS).map(
  ([value, label]) => ({ value, label })
);

/**
 * All target group options for multi-select
 */
const TARGET_GROUP_OPTIONS: Option[] = Object.entries(TARGET_GROUP_LABELS).map(
  ([value, label]) => ({ value, label })
);

/**
 * Region labels (Polish voivodeships with diacritics)
 */
const REGION_LABELS: Record<string, string> = {
  dolnoslaskie: "Dolnośląskie",
  "dolnośląskie": "Dolnośląskie",
  "kujawsko-pomorskie": "Kujawsko-Pomorskie",
  lubelskie: "Lubelskie",
  lubuskie: "Lubuskie",
  lodzkie: "Łódzkie",
  "łódzkie": "Łódzkie",
  malopolskie: "Małopolskie",
  "małopolskie": "Małopolskie",
  mazowieckie: "Mazowieckie",
  opolskie: "Opolskie",
  podkarpackie: "Podkarpackie",
  podlaskie: "Podlaskie",
  pomorskie: "Pomorskie",
  slaskie: "Śląskie",
  "śląskie": "Śląskie",
  swietokrzyskie: "Świętokrzyskie",
  "świętokrzyskie": "Świętokrzyskie",
  "warminsko-mazurskie": "Warmińsko-Mazurskie",
  "warmińsko-mazurskie": "Warmińsko-Mazurskie",
  wielkopolskie: "Wielkopolskie",
  zachodniopomorskie: "Zachodniopomorskie",
};

/**
 * Canonical region options for multi-select (using diacritic forms as values
 * since extracted data may use either form)
 */
const REGION_OPTIONS: Option[] = [
  { value: "dolnośląskie", label: "Dolnośląskie" },
  { value: "kujawsko-pomorskie", label: "Kujawsko-Pomorskie" },
  { value: "lubelskie", label: "Lubelskie" },
  { value: "lubuskie", label: "Lubuskie" },
  { value: "łódzkie", label: "Łódzkie" },
  { value: "małopolskie", label: "Małopolskie" },
  { value: "mazowieckie", label: "Mazowieckie" },
  { value: "opolskie", label: "Opolskie" },
  { value: "podkarpackie", label: "Podkarpackie" },
  { value: "podlaskie", label: "Podlaskie" },
  { value: "pomorskie", label: "Pomorskie" },
  { value: "śląskie", label: "Śląskie" },
  { value: "świętokrzyskie", label: "Świętokrzyskie" },
  { value: "warmińsko-mazurskie", label: "Warmińsko-Mazurskie" },
  { value: "wielkopolskie", label: "Wielkopolskie" },
  { value: "zachodniopomorskie", label: "Zachodniopomorskie" },
];

/**
 * Map grant status to StatusBadge type
 * Uses extraction-* prefix to match StatusBadge color definitions
 */
const getStatusBadgeType = (status: string): StatusType => {
  const statusKey = `extraction-${status}` as StatusType;
  return statusKey;
};

/**
 * Which side of the comparison a value comes from. Dates need it: the target call
 * carries a real UTC instant, the LLM snapshot Warsaw wall time with a bogus "Z".
 */
type ValueSource = "call" | "llm";

interface FormatOptions {
  source?: ValueSource;
  /** Append HH:mm (dates only) - used when two different instants fall on the same day */
  withTime?: boolean;
}

/**
 * Format a date as DD.MM.YYYY (optionally "DD.MM.YYYY HH:mm"), always in
 * Europe/Warsaw, never in the browser time zone: otherwise a deadline "do 23:59"
 * would show two different days in a row that is "unchanged".
 */
const formatDate = (value: unknown, { source = "llm", withTime = false }: FormatOptions = {}): string => {
  const parts =
    source === "call" ? callInstantToWarsawParts(value) : llmWallTimeToWarsawParts(value);
  if (!parts) return "—";
  return withTime ? `${parts.date} ${parts.time}` : parts.date;
};

/**
 * True when two different date values fall on the same Warsaw calendar day, so the
 * row must show the hour to make the difference visible (D-I).
 */
const isSameWarsawDay = (callValue: unknown, llmValue: unknown): boolean => {
  const before = callInstantToWarsawParts(callValue);
  const after = llmWallTimeToWarsawParts(llmValue);
  return !!before && !!after && before.date === after.date;
};

/**
 * Format allocation with thousand separators and PLN.
 * The API serves numeric(15,2) as a string ("300000.00"); numeric strings are
 * formatted like numbers, anything unparseable is shown as is.
 */
const formatAllocation = (value: number | string | null | undefined): string => {
  if (value === null || value === undefined) return "—";
  const parsed = toFiniteNumber(value);
  return `${parsed !== null ? parsed.toLocaleString("pl-PL") : value} PLN`;
};

/**
 * Format array values as comma-separated string with label lookup
 */
const formatArray = (value: string[] | null | undefined, labels?: Record<string, string>): string => {
  if (!value || value.length === 0) return "—";
  if (labels) {
    return value.map((v) => labels[v] || v).join(", ");
  }
  return value.join(", ");
};

/**
 * Format max participants with "osob" suffix
 */
const formatParticipants = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return "—";
  return `${value} osób`;
};

/**
 * Own-property lookup in a label map ("constructor" must not resolve via the prototype)
 */
const labelOf = <T,>(map: Record<string, T>, key: string): T | undefined =>
  hasOwn(map, key) ? map[key] : undefined;

/**
 * Format a field value based on its type
 */
const formatValue = (field: string, value: unknown, options: FormatOptions = {}): string => {
  if (value === null || value === undefined) return "—";

  switch (field) {
    case "applicationStartDate":
    case "applicationEndDate":
      return formatDate(value, options);
    case "allocation":
      return formatAllocation(value as number | string);
    case "targetRegions":
      return formatArray(value as string[], REGION_LABELS);
    case "targetGroups":
      return formatArray(value as string[], TARGET_GROUP_LABELS);
    case "maxParticipants":
      return formatParticipants(value as number);
    case "callNumber":
    case "title":
      return String(value) || "—";
    case "status":
      return labelOf(EXTRACTION_STATUS_LABELS, String(value)) || String(value) || "—";
    case "articleCharacter":
      return articleRoleLabels[String(value)] || String(value) || "—";
    default:
      return String(value) || "—";
  }
};

/**
 * Compare two values for equality
 */
const valuesEqual = (a: unknown, b: unknown): boolean => {
  if (a === b) return true;
  if (a === null && b === undefined) return true;
  if (a === undefined && b === null) return true;
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    const sortedA = [...a].sort();
    const sortedB = [...b].sort();
    return sortedA.every((val, idx) => val === sortedB[idx]);
  }
  return false;
};

/**
 * Equality used to decide whether the LLM proposal differs from the target call.
 * Fields whose two sides use different vocabularies are compared semantically
 * (see fieldChangesNormalization.ts); everything else falls back to valuesEqual.
 */
const fieldValuesEqual = (field: string, before: unknown, after: unknown): boolean => {
  switch (field) {
    case "applicationStartDate":
    case "applicationEndDate":
      return dateValuesEqual(before, after);
    case "targetRegions":
      return regionSetsEqual(before, after);
    case "allocation": {
      // numeric(15,2) arrives as a string ("800000.00"). When either side is not a
      // number the comparison is undecided and falls back to the literal one (D-K):
      // "abc" vs "abc" is unchanged, "abc" vs 100000 a change.
      const numericEqual = numericValuesEqual(before, after);
      return numericEqual !== null ? numericEqual : valuesEqual(before, after);
    }
    case "status":
      return statusValuesEqual(before, after);
    default:
      return valuesEqual(before, after);
  }
};

interface FieldChange {
  field: string;
  label: string;
  before: unknown;
  after: unknown;
  hasChange: boolean;
  /**
   * The moderator always has to judge this row; it is never a "change" and never
   * "unchanged" (rendered as `manual`): articleCharacter, and the regions row while
   * the snapshot carries `regions` that this table cannot show (D-J).
   */
  manualReview: boolean;
}

const toFieldChange = (
  field: string,
  before: unknown,
  after: unknown,
  hasChange: boolean,
  manualReview = false
): FieldChange => ({
  field,
  label: FIELD_LABELS[field] || field,
  before,
  after,
  hasChange,
  manualReview,
});

/**
 * Calculate field changes between target call and extracted data
 */
const calculateChanges = (
  targetCall: RecommendationTargetCall | null,
  extractedData: Record<string, unknown> | null | undefined,
  action: string
): FieldChange[] => {
  const fieldsToCompare = [
    "applicationStartDate",
    "applicationEndDate",
    "allocation",
    "targetRegions",
    "targetGroups",
    "maxParticipants",
    "callNumber",
    "status",
    "articleCharacter",
  ];

  // For CREATE_NEW, show only extracted data (no before values)
  if (action === "create_new" || !targetCall) {
    // Also include title for CREATE_NEW
    const createFields = ["title", ...fieldsToCompare];
    return createFields.map((field) =>
      toFieldChange(
        field,
        null,
        extractedData?.[field] ?? extractedData?.[toCamelCase(field)] ?? null,
        true
      )
    );
  }

  // For UPDATE_EXISTING or LINK_TO_EXISTING, compare values
  return fieldsToCompare.map((field) => {
    // Handle both camelCase and snake_case in extractedData
    const after = extractedData?.[field] ?? extractedData?.[toSnakeCase(field)] ?? extractedData?.[toCamelCase(field)];

    // The article character has no counterpart on the call: the moderator always
    // has to judge it, so it is never a "change" (the row is rendered as "manual").
    if (field === "articleCharacter") {
      return toFieldChange(field, null, after, false, true);
    }

    const before = (targetCall as unknown as Record<string, unknown>)[field];

    // D-J: real snapshots carry the PROJECT regions under `regions`, which this table
    // does not read, while approval of update_existing writes them to the call when
    // they differ. Never claim "unchanged" here - the moderator has to judge (H1).
    const snapshotRegions = extractedData?.regions;
    if (
      field === "targetRegions" &&
      after === undefined &&
      Array.isArray(snapshotRegions) &&
      snapshotRegions.length > 0
    ) {
      return toFieldChange(field, before, after, false, true);
    }

    // Only treat as change if:
    // 1. `after` has a value (null/undefined means LLM didn't extract - not a change)
    // 2. AND values are different
    // This prevents showing "removal" of data when LLM simply didn't extract the field
    const afterHasValue = after !== null && after !== undefined &&
      !(Array.isArray(after) && after.length === 0);
    const hasChange = afterHasValue && !fieldValuesEqual(field, before, after);

    return toFieldChange(field, before, after, hasChange);
  });
};

/**
 * Convert snake_case to camelCase
 */
const toCamelCase = (str: string): string => {
  return str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
};

/**
 * Convert camelCase to snake_case
 */
const toSnakeCase = (str: string): string => {
  return str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
};

/**
 * Check if status change to CANCELLED requires warning
 */
const isCancelledWarning = (
  field: string,
  value: unknown,
  isBefore: boolean
): boolean => {
  if (field !== "status" || isBefore) return false;
  return String(value) === "CANCELLED";
};

/**
 * Render a field value as React element
 * For status field, uses StatusBadge component; a status that is not changing
 * (isChanged=false) is plain text so that it fades together with its row.
 * Rows without a comparison basis (create_new, no target call) are always
 * rendered with isChanged=true, i.e. with badges.
 * "Obecna wartość" (isBefore) carries a CallStatus, "Nowa wartość" the snapshot status.
 */
const renderValue = (
  field: string,
  value: unknown,
  isChanged: boolean,
  isCreateNew: boolean,
  isBefore: boolean
): React.ReactNode => {
  // Handle null/undefined
  if (value === null || value === undefined) {
    return "—";
  }

  // Special handling for status field - use StatusBadge
  if (field === "status") {
    const statusValue = String(value);
    const label =
      (isBefore
        ? labelOf(DIFF_CALL_STATUS_LABELS, statusValue)
        : labelOf(EXTRACTION_STATUS_LABELS, statusValue)) || statusValue;
    if (!isChanged) return label;
    const showCancelledWarning = isCancelledWarning(field, value, isBefore);

    return (
      <span className={showCancelledWarning ? "inline-flex items-center gap-2" : ""}>
        <StatusBadge
          status={
            isBefore
              ? labelOf(DIFF_CALL_STATUS_BADGE_TYPE, statusValue) ?? "unknown"
              : getStatusBadgeType(statusValue)
          }
          label={label}
          // WHY: StatusBadge's default aria-label comes from its own map ("Status: Nieznany"
          // for the neutral badge, "Wyniki ogłoszone" for extraction-RESULTS), which would
          // not match the visible CallStatus label
          aria-label={isBefore ? `Status naboru: ${label}` : undefined}
        />
        {showCancelledWarning && (
          <span
            className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium text-red-700 bg-red-100 border border-red-300 rounded"
            title="Zmiana statusu na Odwolany - nabor zostanie oznaczony jako odwolany"
          >
            <svg
              className="w-3.5 h-3.5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
            Uwaga
          </span>
        )}
      </span>
    );
  }

  // For other fields, return formatted string
  return formatValue(field, value);
};

/**
 * Row state, exposed as `data-change-state` on the <tr> (test/E2E hook):
 * - changed: the LLM proposes a different value than the target call has
 * - unchanged: same value (or nothing proposed) - fades to gray
 * - manual: the moderator decides (articleCharacter; regions while the snapshot
 *   carries `regions` the table cannot show, D-J)
 * Rows without a comparison basis (create_new, no target call) have no state.
 */
type RowState = "changed" | "unchanged" | "manual";

const getRowState = (change: FieldChange, hasComparisonBasis: boolean): RowState | undefined => {
  if (!hasComparisonBasis) return undefined;
  if (change.manualReview) return "manual";
  return change.hasChange ? "changed" : "unchanged";
};

/**
 * Tailwind classes per row state - the single place that decides how a row
 * looks, shared by edit and read-only mode. Neutral-500 is the lightest text
 * allowed (4.83:1 on white, 4.66:1 on amber-50): never use opacity on text.
 * In edit mode the "Nowa wartość" cell keeps neutral-700 because form controls
 * inherit its colour - the controls must stay fully readable (and clickable).
 */
const ROW_PRESENTATION = {
  changed: {
    row: "bg-amber-50",
    label: "font-bold text-neutral-900",
    before: "text-neutral-700",
    after: "font-bold text-amber-700",
  },
  unchanged: {
    row: "",
    label: "text-neutral-500",
    before: "text-neutral-500",
    after: "text-neutral-500",
  },
  // manual rows and rows without a comparison basis
  neutral: {
    row: "",
    label: "text-neutral-700",
    before: "text-neutral-700",
    after: "text-neutral-700",
  },
};

const getRowPresentation = (state: RowState | undefined, editable: boolean) => {
  const presentation =
    state === "changed" || state === "unchanged" ? ROW_PRESENTATION[state] : ROW_PRESENTATION.neutral;
  return editable ? { ...presentation, after: "text-neutral-700" } : presentation;
};

/**
 * Convert ISO datetime to YYYY-MM-DD for date input
 */
const toDateInputValue = (value: unknown): string => {
  if (!value || typeof value !== "string") return "";
  try {
    const date = new Date(value);
    if (isNaN(date.getTime())) return "";
    return date.toISOString().split("T")[0];
  } catch {
    return "";
  }
};

/**
 * Convert YYYY-MM-DD date input value to ISO datetime string
 */
const fromDateInputValue = (value: string): string | null => {
  if (!value) return null;
  return `${value}T00:00:00.000Z`;
};

/**
 * Get the initial editable values from extractedDataSnapshot
 */
const getInitialEditValues = (
  extractedData: Record<string, unknown> | null | undefined
): Record<string, unknown> => {
  if (!extractedData) return {};
  const editableFields = [
    "title",
    "callNumber",
    "applicationStartDate",
    "applicationEndDate",
    "allocation",
    "maxParticipants",
    "regions",
    "targetRegions",
    "targetGroups",
    "status",
    "articleCharacter",
  ];
  const values: Record<string, unknown> = {};
  for (const field of editableFields) {
    const val = extractedData[field] ?? extractedData[toCamelCase(field)];
    if (val !== undefined) {
      values[field] = val;
    }
  }
  return values;
};

export interface FieldChangesTableProps {
  action: "create_new" | "update_existing" | "link_to_existing";
  extractedData: Record<string, unknown> | null | undefined;
  targetCall: RecommendationTargetCall | null;
  isLoading?: boolean;
  /** When true, fields are rendered as editable inputs */
  editable?: boolean;
  /** Callback when field values change. Returns overrides (only changed fields). */
  onValuesChange?: (overrides: RecommendationOverrides, isDirty: boolean) => void;
  /**
   * AC-5: renders a moderator control next to "Numer naboru" allowing the
   * `update_existing` action to be overridden to `create_new` (the only
   * allowed transition). Omit to disable the control entirely; only takes
   * effect when `action === "update_existing"` and `targetCall` is set.
   * `callNumber` for the resulting create_new is read from the regular
   * `onValuesChange` overrides — this callback only carries the on/off decision.
   */
  onActionOverrideChange?: (forceCreateNew: boolean) => void;
}

/**
 * FieldChangesTable - displays diff between current and new values
 *
 * For UPDATE_EXISTING: shows 3 columns (Field, Before, After) with changed rows highlighted
 * For CREATE_NEW: shows 2 columns (Field, Value) since there's no existing data
 * For LINK_TO_EXISTING: shows 3 columns like UPDATE, highlighting any differences
 *
 * When `editable=true`, the "After" / "Value" column renders form inputs.
 * Changed values are reported to parent via `onValuesChange`.
 *
 * Row emphasis (identical in read-only and edit mode, only with a target call):
 * the whole row is marked via `data-change-state` on the `<tr>` (test/E2E hook):
 * - `changed`: amber-50 background, bold label (+ sr-only "zmiana"), bold "After" in read-only mode
 * - `unchanged`: gray label and values (neutral-500), no background
 * - `manual`: "do oceny", never a change - `articleCharacter` (no counterpart on the call)
 *   and the regions row while the snapshot carries `regions` the table cannot show (D-J)
 * - no attribute: `create_new` or no target call - nothing to compare against
 * The state is computed from the LLM proposal vs the target call (statuses, dates,
 * regions and allocation compared semantically), NOT from the moderator's current edits.
 * Dates are always displayed in Europe/Warsaw; a same-day difference shows the hour.
 * In edit mode the controls of gray rows are never disabled, dimmed or `aria-disabled`.
 * A legend below the table explains the highlighting for update/link.
 *
 * @see docs/specifications/2026-03-09-editable-recommendation-fields.md
 * @see docs/specifications/2026-10-21-recommendation-diff-emphasis/spec.md
 */
const FieldChangesTable: React.FC<FieldChangesTableProps> = ({
  action,
  extractedData,
  targetCall,
  isLoading = false,
  editable = false,
  onValuesChange,
  onActionOverrideChange,
}) => {
  // Store the original extracted values for detecting changes
  const originalValuesRef = useRef<Record<string, unknown>>(
    getInitialEditValues(extractedData)
  );
  const [editValues, setEditValues] = useState<Record<string, unknown>>(
    () => getInitialEditValues(extractedData)
  );

  // AC-5: moderator-toggled override of update_existing -> create_new
  const [actionOverrideEnabled, setActionOverrideEnabled] = useState(false);
  const actionOverrideHelpId = useId();

  // Reset edit values when extractedData changes
  useEffect(() => {
    const initial = getInitialEditValues(extractedData);
    originalValuesRef.current = initial;
    setEditValues(initial);
    setActionOverrideEnabled(false);
  }, [extractedData]);

  const handleActionOverrideToggle = useCallback(
    (checked: boolean) => {
      setActionOverrideEnabled(checked);
      onActionOverrideChange?.(checked);
    },
    [onActionOverrideChange]
  );

  /**
   * Calculate overrides (only fields that differ from original extracted values)
   */
  const calculateOverrides = useCallback(
    (currentValues: Record<string, unknown>): RecommendationOverrides => {
      const original = originalValuesRef.current;
      const overrides: RecommendationOverrides = {};

      // Map field names to override keys
      const fieldMapping: Record<string, keyof RecommendationOverrides> = {
        title: "title",
        callNumber: "callNumber",
        applicationStartDate: "applicationStartDate",
        applicationEndDate: "applicationEndDate",
        allocation: "allocation",
        maxParticipants: "maxParticipants",
        targetRegions: "regions",
        targetGroups: "targetGroups",
        status: "status",
        articleCharacter: "articleCharacter",
      };

      for (const [field, overrideKey] of Object.entries(fieldMapping)) {
        const currentVal = currentValues[field];
        const originalVal = original[field];

        if (!valuesEqual(currentVal, originalVal)) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (overrides as any)[overrideKey] = currentVal ?? null;
        }
      }

      return overrides;
    },
    []
  );

  /**
   * Handle field value change
   */
  const handleFieldChange = useCallback(
    (field: string, value: unknown) => {
      setEditValues((prev) => {
        const next = { ...prev, [field]: value };
        const overrides = calculateOverrides(next);
        const isDirty = Object.keys(overrides).length > 0;
        onValuesChange?.(overrides, isDirty);
        return next;
      });
    },
    [calculateOverrides, onValuesChange]
  );

  if (isLoading) {
    return (
      <div className="animate-pulse">
        <div className="h-4 bg-neutral-200 rounded w-1/4 mb-3"></div>
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="flex gap-4 mb-2">
            <div className="h-4 bg-neutral-200 rounded w-1/4"></div>
            <div className="h-4 bg-neutral-200 rounded w-1/3"></div>
            {action !== "create_new" && (
              <div className="h-4 bg-neutral-200 rounded w-1/3"></div>
            )}
          </div>
        ))}
      </div>
    );
  }

  const changes = calculateChanges(targetCall, extractedData, action);
  const isCreateNew = action === "create_new";
  const isLinkToExisting = action === "link_to_existing";
  // Rows are only judged against a real target call (D-F); otherwise there is no basis
  const hasComparisonBasis = !isCreateNew && !!targetCall;
  const hasManualRegions = changes.some(
    (change) => change.field === "targetRegions" && change.manualReview
  );

  /**
   * Render an editable input for a field
   */
  const renderEditableInput = (
    field: string,
    label: string,
    showTime = false
  ): React.ReactNode => {
    const ariaLabel = `Edytuj: ${label}`;
    const currentValue = editValues[field];
    const originalValue = originalValuesRef.current[field];
    const isModified = !valuesEqual(currentValue, originalValue);

    // Tooltip with original LLM value (shown when field has been edited)
    const originalTooltip = isModified
      ? `Oryginalna wartość LLM: ${formatValue(field, originalValue)}`
      : undefined;

    switch (field) {
      case "applicationStartDate":
      case "applicationEndDate":
        return (
          <div>
            <input
              type="date"
              aria-label={ariaLabel}
              value={toDateInputValue(currentValue)}
              onChange={(e) =>
                handleFieldChange(field, fromDateInputValue(e.target.value))
              }
              className="w-full px-2 py-1 text-sm border border-neutral-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
            />
            {/* D-I: the date input has no hour, so a same-day difference shows the LLM hour */}
            {(isModified || showTime) && (
              <p className="mt-1 text-xs text-neutral-500" title={originalTooltip}>
                LLM: {formatValue(field, originalValue, { withTime: showTime })}
              </p>
            )}
          </div>
        );

      case "allocation":
        return (
          <div>
            <input
              type="number"
              aria-label={ariaLabel}
              value={currentValue !== null && currentValue !== undefined ? String(currentValue) : ""}
              onChange={(e) => {
                const val = e.target.value;
                handleFieldChange(field, val === "" ? null : Number(val));
              }}
              min={0}
              step="any"
              className="w-full px-2 py-1 text-sm border border-neutral-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
            />
            {isModified && (
              <p className="mt-1 text-xs text-neutral-500" title={originalTooltip}>
                LLM: {formatValue(field, originalValue)}
              </p>
            )}
          </div>
        );

      case "maxParticipants":
        return (
          <div>
            <input
              type="number"
              aria-label={ariaLabel}
              value={currentValue !== null && currentValue !== undefined ? String(currentValue) : ""}
              onChange={(e) => {
                const val = e.target.value;
                handleFieldChange(field, val === "" ? null : Number(val));
              }}
              min={0}
              step={1}
              className="w-full px-2 py-1 text-sm border border-neutral-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
            />
            {isModified && (
              <p className="mt-1 text-xs text-neutral-500" title={originalTooltip}>
                LLM: {formatValue(field, originalValue)}
              </p>
            )}
          </div>
        );

      case "status":
        return (
          <div>
            <select
              aria-label={ariaLabel}
              value={currentValue ? String(currentValue) : ""}
              onChange={(e) =>
                handleFieldChange(field, e.target.value || null)
              }
              className="w-full px-2 py-1 text-sm border border-neutral-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">-- brak --</option>
              {EXTRACTION_STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            {isModified && (
              <p className="mt-1 text-xs text-neutral-500" title={originalTooltip}>
                LLM: {formatValue(field, originalValue)}
              </p>
            )}
          </div>
        );

      case "articleCharacter": {
        const currentCode = currentValue ? String(currentValue) : "";
        const originalCode = originalValue ? String(originalValue) : "";
        // llmDidNotPropose: LLM returned empty string or "OTHER" (couldn't classify)
        const llmDidNotPropose = originalCode === "" || originalCode === "OTHER";
        return (
          <div>
            <select
              aria-label={ariaLabel}
              value={currentCode}
              onChange={(e) =>
                handleFieldChange(field, e.target.value || null)
              }
              className="w-full px-2 py-1 text-sm border border-neutral-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">-- brak --</option>
              {ARTICLE_CHARACTER_OPTIONS.map((opt) => (
                <option
                  key={opt.value}
                  value={opt.value}
                  title={ARTICLE_CHARACTER_DESCRIPTIONS[opt.value]}
                >
                  {opt.label}
                </option>
              ))}
            </select>
            {/* Always show LLM proposal when LLM gave a concrete (non-OTHER) value */}
            {!llmDidNotPropose && (
              <p className="mt-1 text-xs text-neutral-500" title={originalTooltip}>
                {isModified
                  ? `LLM: ${formatValue(field, originalValue)}`
                  : `LLM zaproponował: ${formatValue(field, originalValue)}`}
              </p>
            )}
            {/* Show amber hint when LLM did not propose a concrete value.
                Suppressing this after moderator confirms OTHER would require
                dedicated "touched" state — deferred per code-review finding #2. */}
            {llmDidNotPropose && (
              <p
                className="mt-1 text-xs text-amber-700"
                title="LLM nie rozpoznał charakteru artykułu — sprawdź ręcznie"
              >
                LLM nie rozpoznał — sprawdź ręcznie
              </p>
            )}
          </div>
        );
      }

      case "targetRegions":
        return (
          <div>
            <MultiSelectDropdown
              options={REGION_OPTIONS}
              selectedValues={Array.isArray(currentValue) ? currentValue as string[] : []}
              onChange={(values) => handleFieldChange(field, values)}
              placeholder="Wybierz regiony..."
              ariaLabel={ariaLabel}
            />
            {isModified && (
              <p className="mt-1 text-xs text-neutral-500" title={originalTooltip}>
                LLM: {formatValue(field, originalValue)}
              </p>
            )}
          </div>
        );

      case "targetGroups":
        return (
          <div>
            <MultiSelectDropdown
              options={TARGET_GROUP_OPTIONS}
              selectedValues={Array.isArray(currentValue) ? currentValue as string[] : []}
              onChange={(values) => handleFieldChange(field, values)}
              placeholder="Wybierz grupy docelowe..."
              ariaLabel={ariaLabel}
            />
            {isModified && (
              <p className="mt-1 text-xs text-neutral-500" title={originalTooltip}>
                LLM: {formatValue(field, originalValue)}
              </p>
            )}
          </div>
        );

      case "callNumber": {
        // AC-5: only update_existing -> create_new is an allowed override, and
        // only when there's an actual colliding call to override away from.
        const showActionOverride =
          action === "update_existing" && !!targetCall && !!onActionOverrideChange;
        const collidesWithTarget =
          showActionOverride &&
          actionOverrideEnabled &&
          valuesEqual(currentValue || null, targetCall?.callNumber ?? null);

        return (
          <div>
            <input
              type="text"
              aria-label={ariaLabel}
              value={currentValue !== null && currentValue !== undefined ? String(currentValue) : ""}
              onChange={(e) =>
                handleFieldChange(field, e.target.value || null)
              }
              className="w-full px-2 py-1 text-sm border border-neutral-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
            />
            {isModified && (
              <p className="mt-1 text-xs text-neutral-500" title={originalTooltip}>
                LLM: {formatValue(field, originalValue)}
              </p>
            )}
            {showActionOverride && (
              <div className="mt-2 pt-2 border-t border-neutral-200">
                <label className="inline-flex items-center gap-2 text-xs text-neutral-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={actionOverrideEnabled}
                    onChange={(e) => handleActionOverrideToggle(e.target.checked)}
                    aria-describedby={actionOverrideEnabled ? actionOverrideHelpId : undefined}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  Utwórz nowy nabór zamiast aktualizacji
                </label>
                {actionOverrideEnabled && (
                  <p id={actionOverrideHelpId} className="mt-1 text-xs text-neutral-500">
                    Wymaga innego numeru naboru niż w kolidującym naborze (pole powyżej).
                  </p>
                )}
                {actionOverrideEnabled && (
                  <p className="mt-1 text-xs text-neutral-500">
                    Koliduje z naborem:{" "}
                    {/* New tab: keeps unsaved field edits and the override decision */}
                    <Link
                      to={`/grants/calls/${targetCall!.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary-600 hover:text-primary-800 underline"
                    >
                      {targetCall!.title} (nr {targetCall!.callNumber || "—"})
                      <svg
                        className="inline-block w-3.5 h-3.5 ml-1 flex-shrink-0"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        aria-hidden="true"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                        />
                      </svg>
                      <span className="sr-only">(otwiera się w nowej karcie)</span>
                    </Link>
                  </p>
                )}
                {collidesWithTarget && (
                  <p className="mt-1 text-xs text-amber-700" role="alert">
                    Numer naboru jest taki sam jak w kolidującym naborze — zmień
                    numer, aby utworzyć nowy nabór.
                  </p>
                )}
              </div>
            )}
          </div>
        );
      }

      case "title":
      default:
        return (
          <div>
            <input
              type="text"
              aria-label={ariaLabel}
              value={currentValue !== null && currentValue !== undefined ? String(currentValue) : ""}
              onChange={(e) =>
                handleFieldChange(field, e.target.value || null)
              }
              className="w-full px-2 py-1 text-sm border border-neutral-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
            />
            {isModified && (
              <p className="mt-1 text-xs text-neutral-500" title={originalTooltip}>
                LLM: {formatValue(field, originalValue)}
              </p>
            )}
          </div>
        );
    }
  };

  return (
    <div className="overflow-x-auto">
      {/* Informational message for LINK_TO_EXISTING */}
      {isLinkToExisting && editable && (
        <div className="mb-3 p-3 bg-primary-50 border border-primary-200 rounded-md">
          <p className="text-sm text-primary-800">
            Pola poniżej można edytować, ale nie wpływają one na istniejący nabór (operacja powiązania).
          </p>
        </div>
      )}
      <table className="min-w-full divide-y divide-neutral-200">
        <thead>
          <tr className="bg-neutral-50">
            <th className="px-4 py-2 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
              Pole
            </th>
            {!isCreateNew && (
              <th className="px-4 py-2 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                Obecna wartość
              </th>
            )}
            <th className="px-4 py-2 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
              {isCreateNew ? "Wartość" : "Nowa wartość"}
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-neutral-100">
          {changes.map((change) => {
            const rowState = getRowState(change, hasComparisonBasis);
            const presentation = getRowPresentation(rowState, editable);
            // D-I: two different instants on the same day -> show the hour on both sides
            const showTime =
              rowState === "changed" &&
              DATE_FIELDS.includes(change.field) &&
              isSameWarsawDay(change.before, change.after);
            const formattedBefore = formatValue(change.field, change.before, {
              source: "call",
              withTime: showTime,
            });
            const formattedAfter = formatValue(change.field, change.after, {
              source: "llm",
              withTime: showTime,
            });

            // Skip row if both values are empty/null (only in read-only mode).
            // Never hide articleCharacter — the moderator must always be able to
            // see/set the article character, even when it is OTHER/empty.
            if (
              !editable &&
              change.field !== "articleCharacter" &&
              formattedBefore === "—" &&
              formattedAfter === "—"
            ) {
              return null;
            }

            // For status field, use StatusBadge rendering (read-only mode)
            const isStatusField = change.field === "status";
            const renderedBefore = isStatusField
              ? renderValue(change.field, change.before, change.hasChange, isCreateNew, true)
              : formattedBefore;
            const renderedStatusAfter = isStatusField
              ? renderValue(change.field, change.after, change.hasChange, isCreateNew, false)
              : null;
            // An unchanged status row may still show two different words (e.g. "Otwarty" |
            // "Wznowiony"): explain on hover that the call's status stays as it is
            const unchangedStatusWithOtherWording =
              isStatusField &&
              rowState === "unchanged" &&
              change.after !== null &&
              change.after !== undefined &&
              renderedStatusAfter !== renderedBefore;
            const renderedAfter = !isStatusField ? (
              formattedAfter
            ) : unchangedStatusWithOtherWording ? (
              <span title="Nie zmienia statusu naboru">{renderedStatusAfter}</span>
            ) : (
              renderedStatusAfter
            );

            return (
              <tr
                key={change.field}
                data-change-state={rowState}
                className={presentation.row || undefined}
              >
                <td className={`px-4 py-2 text-sm ${presentation.label}`}>
                  {change.label}
                  {rowState === "changed" && (
                    <>
                      {" "}
                      <span className="sr-only">zmiana</span>
                    </>
                  )}
                </td>
                {!isCreateNew && (
                  <td className={`px-4 py-2 text-sm ${presentation.before}`}>
                    {renderedBefore}
                  </td>
                )}
                <td className={`px-4 py-2 text-sm ${presentation.after}`}>
                  {editable
                    ? renderEditableInput(change.field, change.label, showTime)
                    : renderedAfter}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {hasComparisonBasis && (
        <p className="mt-2 text-xs text-neutral-500">
          {/* Sample built from the changed-row style, so the legend cannot drift from it */}
          <span className={`${ROW_PRESENTATION.changed.label} ${ROW_PRESENTATION.changed.row} px-1`}>
            Wyróżnione, pogrubione wiersze
          </span>{" "}
          = różnice względem naboru; szare wiersze = bez zmian
          {editable && " (można je nadal edytować)"}.{" "}
          {hasManualRegions
            ? "Charakter artykułu i Regiony (tabela nie pokazuje regionów z propozycji) oceniasz zawsze samodzielnie."
            : "Charakter artykułu oceniasz zawsze samodzielnie."}
          {action === "update_existing" && (
            <>
              {" "}
              <span>
                Aktualizacja zapisze daty, alokację, status, grupy docelowe i regiony; Maks.
                uczestników i Numer naboru są tylko do wglądu.
              </span>
            </>
          )}
          {isLinkToExisting && (
            <>
              {" "}
              <span>Różnice względem naboru nie zostaną zastosowane.</span>
            </>
          )}
        </p>
      )}
    </div>
  );
};

export default FieldChangesTable;
